// Helpers for deciding what a feed item's *main* media is and what still image
// should stand in for it.
//
// Items carry an ordered `media_items` array built server-side
// (backend/src/routes/events/search.js buildMediaItems) and mirrored client-side
// in utils/normalizeEvent.js. The order already respects the author's
// main_content_type preference, so media_items[0] IS the main media.

import { getYouTubeThumbnail, getYouTubeEmbedUrl, extractYouTubeId } from './youtubeHelper';

/**
 * The item's main media, or null when it has none.
 * @returns {{type: 'image'|'video', url: string}|null}
 */
export const getMainMedia = (place) => {
  const items = Array.isArray(place?.media_items) ? place.media_items : null;
  if (!items || items.length === 0) return null;
  const first = items.find((m) => m && m.url);
  return first || null;
};

/** True when the item should be presented as a video first. */
export const isVideoFirst = (place) => getMainMedia(place)?.type === 'video';

/**
 * Still image to show for a video, using the same fallback chain as
 * ReviewMediaCarousel:
 *   1. YouTube thumbnail (hq, then mq)
 *   2. the item's own first image
 *   3. an explicitly supplied fallback (e.g. the card's event image)
 * `failedSrcs` lets a caller skip URLs that already 404'd.
 *
 * @returns {string|null} null means "render a placeholder instead"
 */
export const getVideoPosterSrc = (place, videoUrl, fallbackImage = null, failedSrcs = null) => {
  const rejected = (src) => !src || (failedSrcs && failedSrcs.has(src));

  const hq = getYouTubeThumbnail(videoUrl, 'hq');
  if (!rejected(hq)) return hq;

  const mq = getYouTubeThumbnail(videoUrl, 'mq');
  if (!rejected(mq)) return mq;

  const items = Array.isArray(place?.media_items) ? place.media_items : [];
  const firstImage = items.find((m) => m?.type === 'image' && m.url)?.url;
  if (!rejected(firstImage)) return firstImage;

  if (!rejected(fallbackImage)) return fallbackImage;

  return null;
};

/**
 * Rebuild an ordered media_items array from a raw review row.
 *
 * Mirrors backend/src/routes/events/search.js buildMediaItems(), because the
 * single-review endpoint (GET /reviews/:id) returns the raw table row — it has
 * main_video_url / video_link / main_content_type and an `images` array, but no
 * assembled media_items. Without this, opening the expanded view replaced a
 * feed item that HAD media with a payload that did not, and the carousel went
 * blank a moment after opening.
 *
 * @returns {Array|null} ordered media items, or null when the row has no media
 */
export const buildMediaItemsFromReviewRow = (row) => {
  if (!row) return null;

  const videoItems = [];
  const videoUrl = row.main_video_url || row.video_link;
  if (videoUrl) videoItems.push({ type: 'video', url: videoUrl, display_order: 0 });

  const imageItems = [];
  const rawImages = Array.isArray(row.images)
    ? row.images
    : Array.isArray(row.review_images)
      ? row.review_images
      : [];
  const validImages = rawImages.filter((i) => i && i.image_url);

  if (validImages.length > 0) {
    validImages.forEach((i) => {
      imageItems.push({
        type: 'image',
        url: i.image_url,
        thumbnail_url: i.thumbnail_url || null,
        width: i.width || null,
        height: i.height || null,
        is_main: i.is_main || false,
        display_order: i.display_order ?? 0,
      });
    });
  } else if (row.main_image_url || row.legacy_image_link) {
    imageItems.push({
      type: 'image',
      url: row.main_image_url || row.legacy_image_link,
      display_order: 0,
    });
  }

  const contentType = String(row.main_content_type || 'image').toLowerCase();
  const isVideoMain = contentType === 'video' || contentType === 'video_link';

  const items = isVideoMain
    ? [...videoItems, ...imageItems]
    : [...imageItems, ...videoItems];

  return items.length > 0 ? items : null;
};

/**
 * How a video URL should be played.
 *  - 'inline'   → embeddable, use getInlineEmbedUrl()
 *  - 'external' → not a YouTube URL, open in a new tab
 */
export const getVideoPlayMode = (videoUrl) => (extractYouTubeId(videoUrl) ? 'inline' : 'external');

/** Autoplaying, inline-playable embed URL for a YouTube link. */
export const getInlineEmbedUrl = (videoUrl) => {
  const base = getYouTubeEmbedUrl(videoUrl);
  if (!base) return '';
  return `${base}?autoplay=1&playsinline=1&rel=0`;
};

export default getMainMedia;
